import React, { useId, useState } from "react";
import { Tooltip } from "antd";
import { ThumbsDown } from "lucide-react";
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "./ui/popover";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useChatContext } from "../context";
import type { Message } from "../context";
import type { InsightsData } from "../types";
import { formatInsightsAsMarkdown } from "../utils/insightsUtils";

const trainingAPI = new TrainingAPI();

function deriveReviewContent(aiMessage: Message): string {
    if (aiMessage.content) return aiMessage.content;

    const { type, metadata } = aiMessage;
    const title = metadata?.title ?? "";

    if (type === "table" && metadata?.data) {
        const { headers = [], rows = [] } = metadata.data as { headers?: string[]; rows?: string[][] };
        const headerRow = headers.join(" | ");
        const dataRows = rows.map((r: string[]) => r.join(" | ")).join("\n");
        return `[Table] ${title}\n${headerRow}\n${dataRows}`.trim();
    }

    if (type === "chart" && metadata?.data) {
        const chartType = (metadata.data as { chart?: { type?: string } }).chart?.type ?? "chart";
        return `[Chart: ${chartType}] ${title}`.trim();
    }

    if (type === "stat" && metadata?.data) {
        const d = metadata.data as {
            metric_label?: string;
            value?: number;
            unit?: string;
            player_name?: string;
            delta?: number;
            delta_pct?: number;
            date_range?: string;
        };
        const parts = [
            `[Stat] ${title}`,
            d.player_name && `Player: ${d.player_name}`,
            d.metric_label && `Metric: ${d.metric_label}`,
            d.value != null && `Value: ${d.value}${d.unit ? ` ${d.unit}` : ""}`,
            d.delta != null && `Delta: ${d.delta >= 0 ? "+" : ""}${d.delta} (${d.delta_pct}%)`,
            d.date_range && `Period: ${d.date_range}`,
        ].filter(Boolean);
        return parts.join(" | ").trim();
    }

    if (type === "insights" && metadata?.data) {
        return formatInsightsAsMarkdown(metadata.data as unknown as InsightsData);
    }

    return `[${type ?? "message"}] ${title}`.trim();
}

interface MessageReviewProps {
    // Every AI row of one turn, in order. One review covers the whole turn.
    aiMessages: Message[];
}

const MessageReview: React.FC<MessageReviewProps> = ({ aiMessages }) => {
    const { messages } = useChatContext();
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const titleId = useId();

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // The last row's metadata carries the turnId, the same as every row of the turn.
            const content = aiMessages.map(deriveReviewContent).join("\n\n");
            await trainingAPI.reviewAIMessage(content, aiMessages[aiMessages.length - 1].metadata, messages, notes);
            message.success("Thanks for the feedback!");
            setSubmitted(true);
            setOpen(false);
            setNotes("");
        } catch {
            message.error("Could not submit feedback. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // A submitted review cannot open the form again. The tooltip hides while the form is open,
    // because both sit under the button.
    const label = submitted ? "Feedback submitted" : "Flag this response";
    return (
        <Popover open={open} onOpenChange={(o) => setOpen(o && !submitted)}>
            <Tooltip title={label} open={open ? false : undefined} placement="bottom" overlayClassName="chat-v2-tooltip-sm">
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className={`chat-v2-review-btn${submitted ? " chat-v2-review-btn-submitted" : ""}`}
                        aria-label={label}
                        aria-disabled={submitted}
                    >
                        <ThumbsDown size={14} fill={open || submitted ? "currentColor" : "none"} />
                    </button>
                </PopoverTrigger>
            </Tooltip>
            <PopoverContent side="bottom" align="start" className="chat-v2-review-popover-content" aria-labelledby={titleId}>
                <div className="chat-v2-review-popover">
                    <PopoverHeader>
                        <PopoverTitle id={titleId}>What went wrong?</PopoverTitle>
                    </PopoverHeader>
                    <textarea
                        aria-labelledby={titleId}
                        className="chat-v2-review-textarea"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="(Optional) How should the AI have responded?"
                        rows={3}
                    />
                    <button
                        type="button"
                        className="chat-v2-review-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Submitting…" : "Submit feedback"}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default MessageReview;
